import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, PackageOpen } from "lucide-react";
import { api } from "../lib/api";
import { inputCls } from "../components/site/formPrimitives";
import ShopLayout, { SHOP_CATEGORIES } from "../components/shop/ShopLayout";
import ProductCard from "../components/shop/ProductCard";

const ALL = "Alle produkter";

export default function ShopPage() {
  const [params, setParams] = useSearchParams();
  const category = params.get("kategori") || ALL;
  const [products, setProducts] = useState(null);
  const [q, setQ] = useState(params.get("sok") || "");

  useEffect(() => {
    document.title = "Butikk – MobilHub | Brukte telefoner og tilbehør i Oslo";
    api.get("/shop/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, []);

  const selectCategory = (c) => {
    const next = new URLSearchParams(params);
    if (c === ALL) next.delete("kategori"); else next.set("kategori", c);
    setParams(next);
  };

  const filtered = useMemo(() => {
    if (!products) return [];
    const term = q.trim().toLowerCase();
    return products.filter((p) =>
      (category === ALL || p.category === category) &&
      (!term || [p.name, p.description, p.specs?.model, p.specs?.color, p.specs?.storage, p.specs?.compatible_models]
        .some((v) => (v || "").toLowerCase().includes(term))));
  }, [products, category, q]);

  return (
    <ShopLayout>
      <section className="bg-navy py-12 sm:py-16" data-testid="shop-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-warm">Butikk</p>
          <h1 className="mt-2 font-heading text-3xl font-bold text-white sm:text-4xl lg:text-5xl">Telefoner og tilbehør</h1>
          <p className="mt-3 max-w-2xl text-base text-slate-300 sm:text-lg">
            Send en kjøpsforespørsel, så tar vi kontakt for å bekrefte tilgjengelighet og avtale kjøpet.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10" data-testid="shop-section">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:flex-wrap sm:px-0" data-testid="shop-category-tabs">
            {[ALL, ...SHOP_CATEGORIES].map((c) => (
              <button
                key={c}
                onClick={() => selectCategory(c)}
                className={`shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${category === c ? "border-navy bg-navy text-white" : "border-hairline bg-white text-navy hover:border-electric"}`}
                data-testid={`shop-tab-${c}`}
                aria-pressed={category === c}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="relative lg:w-72">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk i butikken ..." className={`${inputCls} pl-10`} data-testid="shop-search" />
          </div>
        </div>

        {products === null ? (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="aspect-[3/4] animate-pulse rounded-2xl bg-slate-200" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-hairline bg-white p-10 text-center" data-testid="shop-empty">
            <PackageOpen className="mx-auto h-10 w-10 text-ink/30" />
            <p className="mt-3 font-heading text-lg font-bold text-navy">
              {q.trim() ? "Ingen produkter matcher søket" : category === ALL ? "Ingen produkter i butikken ennå" : `Ingen produkter i «${category}» ennå`}
            </p>
            <p className="mt-1 text-sm text-ink/60">Nye produkter legges ut fortløpende. Ta gjerne kontakt om du leter etter noe spesielt.</p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="shop-grid">
            {filtered.map((p) => <ProductCard key={p.id} p={p} />)}
          </div>
        )}
      </section>
    </ShopLayout>
  );
}
